/**
 * Lib.Common: v-date directive
 *
 * @file
 * Sets an element's `textContent` with a date, using local formatting. The modifiers include all of the
 * [$date](#/global/date) formatters. However, using a directive bypasses the Vue render function and can be faster
 * when rendering a large table of information frequently.
 *
 * @modifier iso - ISO 8601 date and time format (2017-06-28T13:06:24-0400).
 * @modifier isodate - ISO 8601 date format (2017-06-28).
 * @modifier hm - Hours and minutes in numeric format (1:06 PM or 13:06).
 * @modifier hms - Hours, minutes, and seconds in numeric format (1:06:24 PM or 13:06:24).
 * @modifier hmsz - Hours, minutes, and seconds in numeric format plus tz (1:06:24 PM EDT or 13:06:24 EDT).
 * @modifier hhmm - Hours and minutes in 2-digit format (01:06 PM or 13:06).
 * @modifier hhmmss - Hours, minutes, and seconds in 2-digit format (01:06:24 PM or 13:06:24).
 * @modifier hhmmssz - Hours, minutes, and seconds in 2-digit format plus tz (01:06:24 PM EDT or 13:06:24 EDT).
 * @modifier m - Month in numeric format (6).
 * @modifier d - Day in numeric format (28).
 * @modifier y - Year in numeric format (2017).
 * @modifier mdyhm - Month, day, year, hour, minute in numeric format (6/28/2017, 1:06 PM).
 * @modifier mdyhms - Month, day, year, hour, minute, second in numeric format (6/28/2017, 1:06:24 PM).
 * @modifier mdyhmsz - Month, day, year, hour, minute, second in numeric format plus tz (6/28/2017, 1:06:24 PM EDT).
 * @modifier mdy - Month, day, and year in numeric format (6/28/2017 or 28/6/2017).
 * @modifier mm - Month in 2-digit format (06).
 * @modifier dd - Day in 2-digit format (28).
 * @modifier yy - Year in 2-digit format (17).
 * @modifier mmm - Month in short format (Jun).
 * @modifier mmmd - Month and day in short/numeric format (Jun 28 or 28 Jun).
 * @modifier mmmy - Month and year in short/numeric format (Jun 2017).
 * @modifier mmmdy - Month, day, and year in short/numeric format (Jun 28, 2017 or 28 Jun 2017).
 * @modifier mmmm - Month in long format (June).
 * @modifier mmmmd - Month and day in long/numeric format (June 28 or 28 June).
 * @modifier mmmmy - Month and year in long/numeric format (June 2017).
 * @modifier mmmmdhm - Month, day, hour, and minute in long/numeric format (June 28, 1:06 PM or 28 June, 13:06).
 * @modifier mmmmdy - Month, day, and year in long/numeric format (June 28, 2017 or 28 June 2017).
 * @modifier wmmmmdy - Weekday, month, day, and year (Wednesday, June 28, 2017 or Wednesday, 28 June 2017).
 * @modifier short - Date and time in short format (Jun 28, 2017, 1:06 PM).
 * @modifier long - Date and time in long format (Wednesday, June 28, 2017, 1:06 PM).
 * @modifier ago - "Just now", "10 min", "3 hrs", etc. Dates not within the past day but within the past year will be formatted as "Jun 28" or "28 Jun". Dates not within the past year will be formatted as "Jun 28, 2020" or "28 Jun 2020".
 * @modifier agoLong - "Just now", "10 minutes", "3 hours", etc. Dates not within the past day but within the past year will be formatted as "June 28" or "28 June". Dates not within the past year will be formatted as "June 28, 2020" or "28 June 2020".
 *
 * @value {string|number|$date} - The date to render.
 */


import $date from '../global/date';
import { dirty } from '../global/date';


const emDash = '—';


function update(el, binding) {
    const value = binding.value;
    const state = el.__date__;

    if (state.d !== dirty) {
        state.ref--;
        bind(el, binding);
    } else if (value !== state.val) {
        const html = state.fn(value);
        const nan = html === emDash;
        if (nan != state.nan) {
            if (nan) {
                el.classList.add('_nan');
            } else {
                el.classList.remove('_nan');
            }
        }
        if (el.__privacy__?.cache !== void 0) {
            el.__privacy__.html = html;
        } else {
            el.innerHTML = html;
        }
        const mod = binding.modifiers;
        state.val = (mod.ago || mod.agoLong) ? void 0 : value;
        state.nan = nan;
    }
}

function bind(el, binding) {
    const mod = binding.modifiers;
    //const boldMonth = mod.boldMonth;
    //const boldDay = mod.boldDay;
    //const boldHour = mod.boldHour;

    let fn =
        mod.iso         ? $date.iso :
        mod.isodate     ? $date.isodate :
        mod.ago         ? $date.ago :
        mod.agoLong     ? $date.agoLong :
        mod.hm          ? $date.hm :
        mod.hms         ? $date.hms :
        mod.hmsz        ? $date.hmsz :
        mod.hhmm        ? $date.hhmm :
        mod.hhmmss      ? $date.hhmmss :
        mod.hhmmssz     ? $date.hhmmssz :
        mod.m           ? $date.m :
        mod.d           ? $date.d :
        mod.y           ? $date.y :
        mod.mdy         ? $date.mdy :
        mod.mdyhm       ? $date.mdyhm :
        mod.mdyhms      ? $date.mdyhms :
        mod.mdyhmsz     ? $date.mdyhmsz :
        mod.mm          ? $date.mm :
        mod.dd          ? $date.dd :
        mod.yy          ? $date.yy :
        mod.mmdd        ? $date.mmdd :
        mod.mmddyy      ? $date.mmddyy :
        mod.mmm         ? $date.mmm :
        mod.mmmd        ? $date.mmmd :
        mod.mmmy        ? $date.mmmy :
        mod.mmmdy       ? $date.mmmdy :
        mod.mmmm        ? $date.mmmm :
        mod.mmmmd       ? $date.mmmmd :
        mod.mmmmy       ? $date.mmmmy :
        mod.mmmmdhm     ? $date.mmmmdhm :
        mod.mmmmdy      ? $date.mmmmdy :
        mod.wmmmmdy     ? $date.wmmmmdy :
        mod.short       ? $date.short :
        mod.long        ? $date.long :
                          $date.mdy;

    if (el.__date__ === void 0) {
        el.__date__ = {
            ref: 0,
            nan: false
        };
    }
    const data = el.__date__;
    data.ref++;
    data.fn = fn;
    data.val = NaN;
    data.d = dirty;
    update(el, binding);
}

function unbind(el) {
    if (!--el.__date__.ref) {
        el.replaceChildren();
        el.classList.remove('_nan');
        delete el.__date__;
    }
}


export default {
    name: 'date',
    directive: {
        bind,
        unbind,
        update
    }
};
